import { isProblemDetails } from '@tms/api-client';
import type { ProblemDetails } from '@tms/api-client';

/**
 * Turning an error response into something a screen can render.
 *
 * A caller branches on `type`, which is a stable URI, and never on `title` or `detail` — those are copy,
 * and copy changes (`STD-003 §4`). The URIs are matched by their last segment so that the host part of the
 * URI can move without every screen needing an edit.
 */
export type ProblemKind = 'validation' | 'rate-limited' | 'unauthorised' | 'conflict' | 'unknown';

/** Field name to the first message for it. One message per field: a form shows one line under one input. */
export type FieldErrors = Readonly<Record<string, string>>;

export interface ApiFailure {
  readonly kind: ProblemKind;
  /** Empty unless the failure was a validation one. */
  readonly fields: FieldErrors;
  /** What a support ticket quotes. Always present on a `ProblemDetails`; empty when the network failed. */
  readonly traceId: string;
}

/**
 * Reads a failed response body. Anything that is not a `ProblemDetails` — a proxy error page, a dropped
 * connection — comes back as `unknown`, which the screens render with the one copy line for
 * "something went wrong, here is the reference".
 */
export function readFailure(body: unknown, status?: number): ApiFailure {
  if (!isProblemDetails(body)) {
    // A body that is not a `ProblemDetails` still arrived with a status, and the status is enough to
    // decide how a screen behaves. This used to answer `unknown` for everything except a `401`, which
    // meant a `409` from behind a proxy — or from any response whose body was not quite the shape
    // `isProblemDetails` wants — was rendered as "something went wrong" rather than as "somebody else
    // edited this". Found by `T-0215`.
    return { kind: kindOfStatus(status), fields: {}, traceId: '' };
  }

  const problem: ProblemDetails = body;

  // The HTTP status wins over the one in the body. They agree in this product — `ApiProblem` sets both —
  // but the response's is the one that actually happened, and a proxy that rewrote a body would otherwise
  // decide how a screen behaves.
  return {
    kind: kindOf(problem, status),
    fields: firstMessagePerField(problem),
    traceId: problem.traceId,
  };
}

/** The kind a bare status implies, for a response with no readable problem body. */
function kindOfStatus(status?: number): ProblemKind {
  switch (status) {
    case 401:
      return 'unauthorised';
    case 409:
      return 'conflict';
    case 422:
      return 'validation';
    case 429:
      return 'rate-limited';
    default:
      return 'unknown';
  }
}

function kindOf(problem: ProblemDetails, status?: number): ProblemKind {
  const httpStatus = status ?? problem.status;
  const segment = problem.type.split('/').pop() ?? '';

  if (segment === 'validation' || httpStatus === 422) {
    return 'validation';
  }
  if (segment === 'rate-limited' || httpStatus === 429) {
    return 'rate-limited';
  }
  if (httpStatus === 401) {
    return 'unauthorised';
  }

  // A `409` is not a failure to retry blindly — somebody else got there first, and the answer carries what
  // they did (`FEAT-011 §5.5`). "Look at this" is a different instruction from "try again", and a screen
  // cannot give it without being able to tell the two apart. Added by `T-0215`.
  if (segment === 'conflict' || httpStatus === 409) {
    return 'conflict';
  }

  return 'unknown';
}

function firstMessagePerField(problem: ProblemDetails): FieldErrors {
  const fields: Record<string, string> = {};

  for (const [field, messages] of Object.entries(problem.errors ?? {})) {
    // The API names fields the way the request body does — camelCase (`STD-003 §2`) — so a screen looks
    // them up by the same name it sent.
    const first = messages[0];
    if (first !== undefined) {
      fields[field] = first;
    }
  }

  return fields;
}
